import React, { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { RichTextEditor } from "@/components/RichTextEditor";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, ImagePlus, Eye, EyeOff } from "lucide-react";

const empty = { title: "", excerpt: "", cover_image: "", content: "", published: true };

export default function AdminBlogPage() {
  const { user, loading: authLoading } = useAuth();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null=list, {}=new, {post}=edit
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/blog", { params: { all: true } }); setPosts(data); }
    catch (e) { console.error("Failed to load blog posts", e); }
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (!authLoading && user && user.role !== "admin") {
    return <div className="min-h-screen bg-[#F7F6F2]"><Header /><div className="text-center py-24 text-[#5C6660]">Admins only.</div></div>;
  }

  const startNew = () => { setForm(empty); setEditing({}); };
  const startEdit = (p) => { setForm({ title: p.title, excerpt: p.excerpt, cover_image: p.cover_image, content: p.content, published: p.published }); setEditing(p); };

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setForm((p) => ({ ...p, cover_image: data.url })); }
    catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Add a title"); return; }
    setSaving(true);
    try {
      if (editing.post_id) await api.put(`/blog/${editing.post_id}`, form);
      else await api.post("/blog", form);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/blog/${p.post_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Header />
      <div className="max-w-[900px] mx-auto px-4 md:px-8 py-10">
        {!editing ? (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-medium text-[#1C211F]">Blog</h1>
                <p className="text-[#4A534E] mt-1">Publish SEO articles for families and planners.</p>
              </div>
              <Button onClick={startNew} className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl" data-testid="new-post-btn"><Plus className="h-4 w-4 mr-1.5" /> New post</Button>
            </div>
            {posts.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-[#E8E6DF] text-[#5C6660]">No posts yet. Create your first article.</div>
            ) : (
              <div className="space-y-3" data-testid="admin-posts">
                {posts.map((p) => (
                  <div key={p.post_id} className="bg-white border border-[#E8E6DF] rounded-2xl p-5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <img src={p.cover_image || "https://images.unsplash.com/photo-1631679706909-1844bbd07221?w=200&q=80"} alt={`Cover image for ${p.title || "untitled post"}`} className="h-12 w-12 rounded-lg object-cover shrink-0" />
                      <div className="min-w-0">
                        <h3 className="font-medium text-[#1C211F] truncate">{p.title}</h3>
                        <div className="flex items-center gap-2 text-xs text-[#5C6660]">
                          {p.published ? <span className="inline-flex items-center gap-1 text-[#0077B6]"><Eye className="h-3 w-3" /> Published</span> : <span className="inline-flex items-center gap-1"><EyeOff className="h-3 w-3" /> Draft</span>}
                          · {new Date(p.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button onClick={() => startEdit(p)} variant="outline" size="sm" className="rounded-xl border-[#E8E6DF]" data-testid="edit-post-btn"><Pencil className="h-4 w-4" /></Button>
                      <Button onClick={() => remove(p)} variant="outline" size="sm" className="rounded-xl border-[#E8E6DF] text-[#C0392B]" data-testid="delete-post-btn"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <button onClick={() => setEditing(null)} className="text-sm text-[#0077B6] font-medium mb-6">← Back to posts</button>
            <h1 className="font-heading text-2xl font-medium text-[#1C211F] mb-6">{editing.post_id ? "Edit post" : "New post"}</h1>
            <div className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="post-title">Title</Label>
                <Input id="post-title" data-testid="post-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="A helpful, search-friendly headline" className="rounded-xl h-12 text-lg" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="post-excerpt">Excerpt (shown in listings & search results)</Label>
                <Textarea id="post-excerpt" data-testid="post-excerpt" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Cover image</Label>
                <div className="flex items-center gap-3">
                  {form.cover_image && <img src={form.cover_image} alt="Cover image preview" className="h-16 w-24 rounded-lg object-cover" />}
                  <label className="inline-flex items-center gap-2 px-4 h-11 rounded-xl border border-[#E8E6DF] cursor-pointer hover:border-[#1A365D] text-sm" data-testid="cover-upload">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Upload image
                    <input type="file" accept="image/*" className="hidden" onChange={upload} />
                  </label>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Content</Label>
                <RichTextEditor value={form.content} onChange={(html) => setForm({ ...form, content: html })} />
              </div>
              <label className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="post-published" /> <span className="text-sm text-[#2A312D]">Published</span></label>
              <Button onClick={save} disabled={saving} className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11 px-8" data-testid="save-post-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save post"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
